import { Button, Input, TreeSelect, message } from "antd";
import { TreeSelectProps } from "antd/lib/tree-select";
import { JSONSchema7 } from "json-schema";
import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  iconFromInternalType,
  internalSchemaTypeFromSchema,
} from "@blings/dynamic-form";
import { sharedSelectAllowClear } from "../components/antd-extensions/selectClearIcon";

interface ITreeSelectProps extends TreeSelectProps<any> {
  json: JSONSchema7;
  onAddDataKey?: (key: string) => Promise<boolean> | boolean;
}

const isJsonSchemaObject = (value: unknown): value is JSONSchema7 =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
const getNodeTitle = (
  label: string,
  schema?: JSONSchema7,
  fullPath?: string
) => {
  const schemaType = schema ? internalSchemaTypeFromSchema(schema) : undefined;

  return (
    <span
      className="json-tree-select-node"
      title={fullPath && fullPath !== label ? fullPath : undefined}
    >
      {schemaType && (
        <span className="json-tree-select-node__icon">
          {iconFromInternalType(schemaType, 1.8)}
        </span>
      )}
      <span className="json-tree-select-node__label">{label}</span>
    </span>
  );
};

function getNodes(schema: JSONSchema7 = {}, path = ""): any[] {
  if (!schema) return [];

  const nodes: any[] = [];

  // Handle properties in objects
  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, value]) => {
      const nkey = [path, key].filter((e) => e).join(".");
      const schemaValue = isJsonSchemaObject(value) ? value : undefined;
      const nodeIsLeaf =
        !schemaValue ||
        (!schemaValue.properties &&
          !(
            schemaValue.items &&
            typeof schemaValue.items === "object" &&
            !Array.isArray(schemaValue.items)
          ));
      const nodeTitle = nodeIsLeaf ? getNodeTitle(key, schemaValue, nkey) : key;
      const node: any = {
        title: nodeTitle,
        value: nkey,
        isLeaf: nodeIsLeaf,
        selectable: true,
        fullLabel: nodeIsLeaf
          ? getNodeTitle(nkey, schemaValue, nkey)
          : nodeTitle,
      };

      if (schemaValue && typeof schemaValue === "object") {
        const children = getNodes(schemaValue as JSONSchema7, nkey);
        if (children.length > 0) {
          node.children = children;
          node.selectable = false;
        }
      }

      nodes.push(node);
    });
  }

  // Handle array items
  if (
    schema.items &&
    typeof schema.items === "object" &&
    !Array.isArray(schema.items)
  ) {
    const itemNodes = getNodes(schema.items as JSONSchema7, path);
    nodes.push(...itemNodes);
  }

  return nodes;
}

const DEFAULT_PLACEHOLDER = "Pick a Data Source";

export const JSONTreeSelect = ({
  json,
  onAddDataKey,
  className,
  popupClassName,
  ...rest
}: ITreeSelectProps) => {
  const [isAddingNewKey, setIsAddingNewKey] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    onDropdownVisibleChange,
    getPopupContainer,
    treeNodeLabelProp,
    dropdownRender: dropdownRenderProp,
    popupRender: popupRenderProp,
    ...treeSelectProps
  } = rest;
  const {
    allowClear: allowClearProp,
    ...remainingTreeSelectProps
  } = treeSelectProps;

  const treeData = useMemo(() => getNodes(json), [json]);
  const resolvedClassName = ["blings-select-controls", className]
    .filter(Boolean)
    .join(" ");
  const resolvedPopupClass =
    popupClassName || (className ? `${className}-dropdown` : undefined);
  const resolvedAllowClear =
    allowClearProp === true ? sharedSelectAllowClear : allowClearProp;

  const resetAddState = useCallback(() => {
    setIsAddingNewKey(false);
    setNewKey("");
    setIsSaving(false);
  }, []);

  const handleSaveNewKey = useCallback(async () => {
    if (!onAddDataKey) return;
    const trimmedKey = newKey.trim();
    if (!trimmedKey) {
      message.warning("Please provide a data key name.");
      return;
    }
    if (
      json?.properties &&
      Object.prototype.hasOwnProperty.call(json.properties, trimmedKey)
    ) {
      message.warning("This key already exists.");
      return;
    }
    try {
      setIsSaving(true);
      const result = await onAddDataKey(trimmedKey);
      if (result === false) {
        return;
      }
      resetAddState();
      setDropdownOpen(false);
    } catch (error) {
      console.error("Failed to add data key", error);
      message.error("Could not add the data key. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [json, newKey, onAddDataKey, resetAddState]);

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (onAddDataKey) {
        setDropdownOpen(open);
        if (!open) {
          resetAddState();
        }
      }
      onDropdownVisibleChange?.(open);
    },
    [onAddDataKey, onDropdownVisibleChange, resetAddState]
  );

  const resolvePopupContainer = useCallback(
    (triggerNode: HTMLElement) => {
      const customContainer = getPopupContainer?.(triggerNode);
      if (customContainer) {
        return customContainer;
      }

      const protectedContainer = triggerNode.closest(
        "[data-layer-inspector-keep-selection]"
      );
      if (protectedContainer instanceof HTMLElement) {
        return protectedContainer;
      }

      return triggerNode.parentElement ?? triggerNode.ownerDocument.body;
    },
    [getPopupContainer]
  );

  const dropdownRender = useCallback(
    (menu: ReactNode) => (
      <div
        className="json-tree-select-dropdown"
        data-layer-inspector-keep-selection
      >
        {menu}
        {onAddDataKey && (
          <div className="json-tree-select-add">
            {isAddingNewKey ? (
              <div className="json-tree-select-add__form">
                <Input
                  size="middle"
                  placeholder="Data key name"
                  value={newKey}
                  disabled={isSaving}
                  onChange={(event) => setNewKey(event.target.value)}
                  onPressEnter={handleSaveNewKey}
                  autoFocus
                />
                <div className="json-tree-select-add__actions">
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleSaveNewKey}
                    disabled={!newKey.trim()}
                    loading={isSaving}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    onClick={resetAddState}
                    disabled={isSaving}
                    type="text"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="text"
                className="json-tree-select-add__trigger"
                onClick={() => setIsAddingNewKey(true)}
                block
              >
                + Add new data
              </Button>
            )}
          </div>
        )}
      </div>
    ),
    [
      handleSaveNewKey,
      isAddingNewKey,
      isSaving,
      newKey,
      onAddDataKey,
      resetAddState,
    ]
  );

  return (
    <TreeSelect
      {...remainingTreeSelectProps}
      allowClear={resolvedAllowClear}
      open={onAddDataKey ? dropdownOpen : remainingTreeSelectProps.open}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      getPopupContainer={resolvePopupContainer}
      showSearch
      className={resolvedClassName}
      popupClassName={resolvedPopupClass}
      style={{ width: "100%" }}
      treeData={treeData}
      placeholder={rest.placeholder || DEFAULT_PLACEHOLDER}
      treeDefaultExpandAll
      treeNodeLabelProp={treeNodeLabelProp ?? "fullLabel"}
      treeNodeFilterProp="value"
      popupRender={
        onAddDataKey ? dropdownRender : popupRenderProp ?? dropdownRenderProp
      }
    />
  );
};
